import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { apiRequest } from "../../../packages/ui/src/runtime-client.js";
import { MOBILE_STORE_PRODUCTS, type MobileStoreProductId } from "./native-release.js";

export type DeviceStorePlatform = "APPLE" | "GOOGLE";

export interface MobileStoreProductDetails {
  readonly id: MobileStoreProductId;
  readonly name: string;
  readonly description: string;
  readonly type: "subs" | "inapp";
  readonly formattedPrice?: string;
  readonly billingPeriod?: string;
  readonly basePlanId?: string;
}

interface RawProductDetails {
  readonly id: string;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly type?: string;
  readonly formattedPrice?: string;
  readonly displayPrice?: string;
  readonly billingPeriod?: string;
  readonly basePlanId?: string;
}

export interface AndroidPurchaseEvidence {
  readonly platform: "GOOGLE";
  readonly packageName: "com.portability.app";
  readonly productIds: readonly MobileStoreProductId[];
  readonly productId?: MobileStoreProductId;
  readonly purchaseToken: string;
  readonly orderId?: string;
  readonly purchaseState: number;
  readonly acknowledged: boolean;
  readonly serverEntitlementGranted: false;
  readonly serverVerificationRequired: true;
}

export interface AndroidPurchaseResult {
  readonly responseCode: number;
  readonly purchases: readonly AndroidPurchaseEvidence[];
  readonly serverEntitlementAuthority: true;
}

export interface AppleTransactionEvidence {
  readonly status: "SERVER_VERIFICATION_REQUIRED";
  readonly platform: "APPLE";
  readonly productId: MobileStoreProductId;
  readonly transactionId: string;
  readonly originalTransactionId: string;
  readonly signedTransactionJws: string;
  readonly environment: string;
  readonly appAccountToken?: string;
  readonly serverEntitlementGranted: false;
  readonly finishAfterServerVerification: true;
}

interface ApplePurchaseStatus {
  readonly status: "PENDING" | "CANCELLED";
  readonly productId: MobileStoreProductId;
  readonly serverEntitlementGranted: false;
}

interface AppleRestoreResult {
  readonly synced: true;
  readonly transactions: readonly AppleTransactionEvidence[];
  readonly serverReconciliationRequired: true;
}

interface PortabilityBillingPlugin {
  queryProducts(options: { readonly productIds: readonly MobileStoreProductId[] }): Promise<{ readonly products: readonly RawProductDetails[]; readonly serverEntitlementAuthority: true }>;
  purchase(options: { readonly productId: MobileStoreProductId; readonly obfuscatedAccountId?: string; readonly appAccountToken?: string; readonly oldPurchaseToken?: string; readonly replacementMode?: GoogleSubscriptionReplacementMode }): Promise<AndroidPurchaseResult | AppleTransactionEvidence | ApplePurchaseStatus>;
  currentEntitlements(): Promise<AndroidPurchaseResult | AppleRestoreResult>;
  restorePurchases(options: { readonly explicitUserAction: true }): Promise<AndroidPurchaseResult | AppleRestoreResult>;
  finishServerVerifiedTransaction(options: { readonly serverVerified: true; readonly transactionId: string }): Promise<{ readonly finished: true; readonly transactionId: string }>;
  openManageSubscriptions(): Promise<void>;
  addListener(eventName: "purchaseUpdate" | "transactionUpdate", listener: (result: AndroidPurchaseResult | AppleTransactionEvidence) => void): Promise<PluginListenerHandle>;
}

const PortabilityBilling = registerPlugin<PortabilityBillingPlugin>("PortabilityBilling");

type GoogleSubscriptionReplacementMode = "CHARGE_FULL_PRICE" | "DEFERRED";
const GOOGLE_PLAY_RESPONSE_OK = 0;
const GOOGLE_PLAY_RESPONSE_USER_CANCELED = 1;

export const MOBILE_STORE_SUBSCRIPTION_IDS = Object.freeze([
  MOBILE_STORE_PRODUCTS.insiderMonthly.id,
  MOBILE_STORE_PRODUCTS.creatorMonthly.id
]);
export const MOBILE_STORE_ONE_TIME_PRODUCT_IDS = Object.freeze([
  MOBILE_STORE_PRODUCTS.kidsDownload.id,
  MOBILE_STORE_PRODUCTS.creatorTipSmall.id,
  MOBILE_STORE_PRODUCTS.creatorTipMedium.id,
  MOBILE_STORE_PRODUCTS.creatorTipLarge.id
]);
export const MOBILE_STORE_APPROVED_PRODUCT_IDS = Object.freeze([...MOBILE_STORE_SUBSCRIPTION_IDS, ...MOBILE_STORE_ONE_TIME_PRODUCT_IDS]);
export const ANDROID_SUBSCRIPTION_IDS = MOBILE_STORE_SUBSCRIPTION_IDS;
export const ANDROID_ONE_TIME_PRODUCT_IDS = MOBILE_STORE_ONE_TIME_PRODUCT_IDS;
export const ANDROID_APPROVED_PRODUCT_IDS = MOBILE_STORE_APPROVED_PRODUCT_IDS;

export interface MobileStorePurchaseSubmission {
  readonly platform: DeviceStorePlatform;
  readonly status: string;
  readonly productId: MobileStoreProductId;
  readonly purchaseActivityId: string;
  readonly purchaseToken?: string;
  readonly transactionId?: string;
  readonly signedTransactionJws?: string;
}

export type AndroidPurchaseSubmission = MobileStorePurchaseSubmission & Readonly<{ platform: "GOOGLE"; purchaseToken: string }>;

export function deviceStorePlatform(): DeviceStorePlatform | null {
  if (!Capacitor.isNativePlatform()) return null;
  if (Capacitor.getPlatform() === "android") return "GOOGLE";
  if (Capacitor.getPlatform() === "ios") return "APPLE";
  return null;
}

export function mobileStoreBillingAvailable(): boolean { return deviceStorePlatform() !== null; }
export function androidBillingAvailable(): boolean { return deviceStorePlatform() === "GOOGLE"; }
export function appleBillingAvailable(): boolean { return deviceStorePlatform() === "APPLE"; }
export function mobileStoreName(): "App Store" | "Google Play" { return deviceStorePlatform() === "APPLE" ? "App Store" : "Google Play"; }

async function obfuscateAccountId(accountId: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(accountId));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function approvedProductId(value: string): value is MobileStoreProductId {
  return (MOBILE_STORE_APPROVED_PRODUCT_IDS as readonly string[]).includes(value);
}

function normalizeProducts(products: readonly RawProductDetails[]): readonly MobileStoreProductDetails[] {
  return products.flatMap((product) => {
    if (!approvedProductId(product.id)) return [];
    const isSubscription = (MOBILE_STORE_SUBSCRIPTION_IDS as readonly string[]).includes(product.id);
    return [Object.freeze({
      id: product.id,
      name: product.name ?? product.displayName ?? product.id,
      description: product.description ?? "",
      type: isSubscription ? "subs" as const : "inapp" as const,
      ...(product.formattedPrice === undefined && product.displayPrice === undefined ? {} : { formattedPrice: product.formattedPrice ?? product.displayPrice }),
      ...(product.billingPeriod === undefined ? {} : { billingPeriod: product.billingPeriod }),
      ...(product.basePlanId === undefined ? {} : { basePlanId: product.basePlanId })
    })];
  });
}

function validateAndroidPurchaseEvidence(purchase: AndroidPurchaseEvidence): void {
  if (purchase.platform !== "GOOGLE" || purchase.packageName !== "com.portability.app") throw new Error("Google Play purchase identity mismatch");
  if (!purchase.purchaseToken.trim()) throw new Error("Google Play did not return a purchase token");
  if (purchase.productIds.length !== 1 || purchase.productId !== purchase.productIds[0]) throw new Error("Google Play purchase must contain exactly one approved product");
  if (purchase.productId === undefined || !approvedProductId(purchase.productId)) throw new Error("Google Play returned an unexpected Portability product");
}

function validateAppleEvidence(purchase: AppleTransactionEvidence, expectedAccountToken: string | null): void {
  if (purchase.platform !== "APPLE" || !approvedProductId(purchase.productId)) throw new Error("App Store purchase identity mismatch");
  if (!purchase.transactionId.trim() || !purchase.signedTransactionJws.trim()) throw new Error("App Store signed transaction evidence is required");
  if (expectedAccountToken !== null && purchase.appAccountToken?.toLowerCase() !== expectedAccountToken.toLowerCase()) throw new Error("App Store purchase is not bound to this Portability account");
}

async function queryProducts(productIds: readonly MobileStoreProductId[]): Promise<readonly MobileStoreProductDetails[]> {
  if (!mobileStoreBillingAvailable()) return [];
  const result = await PortabilityBilling.queryProducts({ productIds });
  return normalizeProducts(result.products);
}

export function queryMobileStoreMembershipProducts() { return queryProducts(MOBILE_STORE_SUBSCRIPTION_IDS); }
export function queryMobileStoreOneTimeProducts() { return queryProducts(MOBILE_STORE_ONE_TIME_PRODUCT_IDS); }
export const queryAndroidMembershipProducts = queryMobileStoreMembershipProducts;
export const queryAndroidOneTimeProducts = queryMobileStoreOneTimeProducts;

export async function submitAndroidPurchaseEvidence(_accountId: string, result: AndroidPurchaseResult, qualificationAccount: string | null): Promise<readonly AndroidPurchaseSubmission[]> {
  if (result.responseCode === GOOGLE_PLAY_RESPONSE_USER_CANCELED) return [];
  if (result.responseCode !== GOOGLE_PLAY_RESPONSE_OK) throw new Error(`Google Play purchase failed with response code ${result.responseCode}`);
  const submissions: AndroidPurchaseSubmission[] = [];
  for (const purchase of result.purchases) {
    if (purchase.productId === undefined || !approvedProductId(purchase.productId)) continue;
    validateAndroidPurchaseEvidence(purchase);
    if (purchase.purchaseState === 2) {
      submissions.push({ platform: "GOOGLE", status: "PENDING", productId: purchase.productId, purchaseActivityId: "", purchaseToken: purchase.purchaseToken });
      continue;
    }
    if (purchase.purchaseState !== 1) continue;
    const response = await apiRequest<Omit<AndroidPurchaseSubmission, "platform" | "purchaseToken">>("/v1/mobile-store/purchases", {
      method: "POST",
      qualificationAccount,
      body: { platform: "GOOGLE", packageName: purchase.packageName, productId: purchase.productId, purchaseToken: purchase.purchaseToken, environment: "AUTO" }
    });
    submissions.push({ ...response, platform: "GOOGLE", purchaseToken: purchase.purchaseToken });
  }
  return submissions;
}

async function submitApplePurchaseEvidence(purchase: AppleTransactionEvidence, appAccountToken: string | null, qualificationAccount: string | null): Promise<MobileStorePurchaseSubmission> {
  validateAppleEvidence(purchase, appAccountToken);
  const response = await apiRequest<{ readonly status: string; readonly productId: MobileStoreProductId; readonly purchaseActivityId: string; readonly finishOnDevice: boolean }>("/v1/mobile-store/purchases", {
    method: "POST",
    qualificationAccount,
    body: { platform: "APPLE", packageName: "com.portability.app", productId: purchase.productId, transactionId: purchase.transactionId, signedTransactionJws: purchase.signedTransactionJws, environment: "AUTO" }
  });
  if (response.finishOnDevice && (MOBILE_STORE_SUBSCRIPTION_IDS as readonly string[]).includes(purchase.productId)) {
    await PortabilityBilling.finishServerVerifiedTransaction({ serverVerified: true, transactionId: purchase.transactionId });
  }
  return Object.freeze({ platform: "APPLE" as const, status: response.status, productId: response.productId, purchaseActivityId: response.purchaseActivityId, transactionId: purchase.transactionId, signedTransactionJws: purchase.signedTransactionJws });
}

export async function watchMobileStorePurchaseUpdates(accountId: string, appAccountToken: string | null, qualificationAccount: string | null, listener: (result: readonly MobileStorePurchaseSubmission[]) => void, onError: (error: unknown) => void): Promise<PluginListenerHandle | null> {
  const platform = deviceStorePlatform();
  if (platform === null) return null;
  return PortabilityBilling.addListener(platform === "APPLE" ? "transactionUpdate" : "purchaseUpdate", (result) => {
    const submissions = platform === "APPLE"
      ? submitApplePurchaseEvidence(result as AppleTransactionEvidence, appAccountToken, qualificationAccount).then((item) => [item])
      : submitAndroidPurchaseEvidence(accountId, result as AndroidPurchaseResult, qualificationAccount);
    void submissions.then(listener).catch(onError);
  });
}

export function watchAndroidMembershipPurchaseUpdates(accountId: string, qualificationAccount: string | null, listener: (result: readonly MobileStorePurchaseSubmission[]) => void, onError: (error: unknown) => void) {
  return watchMobileStorePurchaseUpdates(accountId, null, qualificationAccount, listener, onError);
}
export const watchAndroidPurchaseUpdates = watchAndroidMembershipPurchaseUpdates;

function androidSubscriptionReplacement(result: AndroidPurchaseResult, targetProductId: MobileStoreProductId): Readonly<{ oldPurchaseToken: string; replacementMode: GoogleSubscriptionReplacementMode }> | undefined {
  const activeSubscriptions: AndroidPurchaseEvidence[] = [];
  for (const purchase of result.purchases) {
    if (purchase.purchaseState !== 1) continue;
    if (purchase.productId === undefined || !(MOBILE_STORE_SUBSCRIPTION_IDS as readonly string[]).includes(purchase.productId)) continue;
    validateAndroidPurchaseEvidence(purchase);
    activeSubscriptions.push(purchase);
  }
  if (activeSubscriptions.some(({ productId }) => productId === targetProductId)) {
    throw new Error("This Google Play membership is already active. Use Manage in Google Play to review it.");
  }
  const replacementTokens = [...new Set(activeSubscriptions.map(({ purchaseToken }) => purchaseToken))];
  if (replacementTokens.length === 0) return undefined;
  if (replacementTokens.length > 1) throw new Error("Multiple active Google Play memberships were found. Manage them in Google Play before changing plans.");
  return Object.freeze({
    oldPurchaseToken: replacementTokens[0]!,
    replacementMode: targetProductId === MOBILE_STORE_PRODUCTS.creatorMonthly.id ? "CHARGE_FULL_PRICE" : "DEFERRED"
  });
}

export async function purchaseMobileStoreProduct(accountId: string, appAccountToken: string | null, productId: MobileStoreProductId, qualificationAccount: string | null): Promise<readonly MobileStorePurchaseSubmission[]> {
  const platform = deviceStorePlatform();
  if (platform === null) throw new Error("Mobile-store purchases are available only in the iOS or Android app");
  if (!approvedProductId(productId)) throw new Error("Only approved Portability products may be purchased here");
  if (platform === "GOOGLE") {
    const replacement = (MOBILE_STORE_SUBSCRIPTION_IDS as readonly string[]).includes(productId)
      ? androidSubscriptionReplacement(await PortabilityBilling.currentEntitlements() as AndroidPurchaseResult, productId)
      : undefined;
    const result = await PortabilityBilling.purchase({
      productId,
      obfuscatedAccountId: await obfuscateAccountId(accountId),
      ...(replacement === undefined ? {} : replacement)
    });
    return submitAndroidPurchaseEvidence(accountId, result as AndroidPurchaseResult, qualificationAccount);
  }
  if (appAccountToken === null) throw new Error("A server-issued App Store account token is required");
  const result = await PortabilityBilling.purchase({ productId, appAccountToken });
  if ((result as ApplePurchaseStatus).status === "PENDING") return [{ platform: "APPLE", status: "PENDING", productId, purchaseActivityId: "" }];
  if ((result as ApplePurchaseStatus).status === "CANCELLED") return [];
  return [await submitApplePurchaseEvidence(result as AppleTransactionEvidence, appAccountToken, qualificationAccount)];
}

export function purchaseAndroidProduct(accountId: string, productId: MobileStoreProductId, qualificationAccount: string | null) {
  if (!androidBillingAvailable()) throw new Error("Google Play purchases are available only in the Android app");
  return purchaseMobileStoreProduct(accountId, null, productId, qualificationAccount);
}

export async function finalizeMobileStoreConsumablePurchase(purchase: MobileStorePurchaseSubmission, qualificationAccount: string | null): Promise<void> {
  if (!(MOBILE_STORE_ONE_TIME_PRODUCT_IDS as readonly string[]).includes(purchase.productId)) throw new Error("Only approved one-time purchases may be finalized here");
  if (!purchase.purchaseActivityId) throw new Error("Verified mobile-store purchase evidence is required before fulfillment finalization");
  const body = purchase.platform === "GOOGLE"
    ? { platform: "GOOGLE", purchaseActivityId: purchase.purchaseActivityId, productId: purchase.productId, purchaseToken: purchase.purchaseToken }
    : { platform: "APPLE", purchaseActivityId: purchase.purchaseActivityId, productId: purchase.productId, transactionId: purchase.transactionId, signedTransactionJws: purchase.signedTransactionJws };
  const response = await apiRequest<{ readonly finishOnDevice?: boolean }>("/v1/mobile-store/purchases/finalize", { method: "POST", qualificationAccount, body });
  if (purchase.platform === "APPLE" && response.finishOnDevice === true && purchase.transactionId !== undefined) {
    await PortabilityBilling.finishServerVerifiedTransaction({ serverVerified: true, transactionId: purchase.transactionId });
  }
}

export async function finalizeAndroidConsumablePurchase(purchase: MobileStorePurchaseSubmission, qualificationAccount: string | null) {
  return finalizeMobileStoreConsumablePurchase(purchase, qualificationAccount);
}

export function purchaseMobileStoreMembership(accountId: string, appAccountToken: string | null, productId: MobileStoreProductId, qualificationAccount: string | null) {
  if (!(MOBILE_STORE_SUBSCRIPTION_IDS as readonly string[]).includes(productId)) throw new Error("Only approved monthly memberships may be purchased here");
  return purchaseMobileStoreProduct(accountId, appAccountToken, productId, qualificationAccount);
}

export function purchaseAndroidMembership(accountId: string, productId: MobileStoreProductId, qualificationAccount: string | null) {
  return purchaseMobileStoreMembership(accountId, null, productId, qualificationAccount);
}

export async function restoreMobileStorePurchases(accountId: string, appAccountToken: string | null, qualificationAccount: string | null): Promise<readonly MobileStorePurchaseSubmission[]> {
  const platform = deviceStorePlatform();
  if (platform === null) throw new Error("Mobile-store restore is available only in the iOS or Android app");
  const result = await PortabilityBilling.restorePurchases({ explicitUserAction: true });
  if (platform === "GOOGLE") return submitAndroidPurchaseEvidence(accountId, result as AndroidPurchaseResult, qualificationAccount);
  const submissions: MobileStorePurchaseSubmission[] = [];
  for (const transaction of (result as AppleRestoreResult).transactions) submissions.push(await submitApplePurchaseEvidence(transaction, appAccountToken, qualificationAccount));
  return submissions;
}

export function restoreAndroidPurchases(accountId: string, qualificationAccount: string | null) {
  if (!androidBillingAvailable()) throw new Error("Google Play restore is available only in the Android app");
  return restoreMobileStorePurchases(accountId, null, qualificationAccount);
}
export const restoreAndroidMemberships = restoreAndroidPurchases;

export async function reconcileMobileStoreEntitlements(accountId: string, appAccountToken: string | null, qualificationAccount: string | null): Promise<readonly MobileStorePurchaseSubmission[]> {
  const platform = deviceStorePlatform();
  if (platform === null) return [];
  const result = await PortabilityBilling.currentEntitlements();
  if (platform === "GOOGLE") return submitAndroidPurchaseEvidence(accountId, result as AndroidPurchaseResult, qualificationAccount);
  const submissions: MobileStorePurchaseSubmission[] = [];
  for (const transaction of (result as AppleRestoreResult).transactions) submissions.push(await submitApplePurchaseEvidence(transaction, appAccountToken, qualificationAccount));
  return submissions;
}

export async function openMobileStoreSubscriptionManagement(): Promise<void> {
  if (!mobileStoreBillingAvailable()) throw new Error("Mobile-store subscription management is available only in the iOS or Android app");
  await PortabilityBilling.openManageSubscriptions();
}

export const openAndroidSubscriptionManagement = openMobileStoreSubscriptionManagement;
